import { create } from 'zustand';
import api from '../lib/api';
import type {
  DashboardStats,
  PipelineCounts,
  TemperatureCounts,
  DashboardActivity,
  DashboardTask,
  DashboardUpcomingActivity,
} from '../types';

interface DashboardState {
  // Stats data
  pipeline: PipelineCounts;
  temperature: TemperatureCounts;
  recentActivities: DashboardActivity[];
  upcomingTasks: DashboardTask[];
  upcomingActivities: DashboardUpcomingActivity[];
  totalPeople: number;
  peopleWithActivity7Days: number;

  // Recently viewed
  recentPeople: any[];
  recentOrgs: any[];
  churnedPeople: any[];
  isLoadingRecent: boolean;

  // Loading state
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: string | null;

  // Actions
  fetchStats: () => Promise<void>;
  fetchRecentViews: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

// Default empty counts
const defaultPipeline: PipelineCounts = {
  LEAD: 0,
  PROSPECT: 0,
  CUSTOMER: 0,
  CHURNED: 0,
};

const defaultTemperature: TemperatureCounts = {
  HOT: 0,
  WARM: 0,
  COLD: 0,
  FREEZING: 0,
  unset: 0,
};

export const useDashboardStore = create<DashboardState>((set, get) => ({
  // Initial state
  pipeline: defaultPipeline,
  temperature: defaultTemperature,
  recentActivities: [],
  upcomingTasks: [],
  upcomingActivities: [],
  totalPeople: 0,
  peopleWithActivity7Days: 0,

  recentPeople: [],
  recentOrgs: [],
  churnedPeople: [],
  isLoadingRecent: false,

  isLoading: false,
  error: null,
  lastFetchedAt: null,

  // ==================== ACTIONS ====================

  /**
   * Fetch dashboard statistics from the API
   */
  fetchStats: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await api.get('/api/dashboard/stats');
      const data: DashboardStats = response.data;

      set({
        pipeline: data.pipeline,
        temperature: data.temperature,
        recentActivities: data.recentActivities,
        upcomingTasks: data.upcomingTasks,
        upcomingActivities: data.upcomingActivities ?? [],
        totalPeople: data.totalPeople,
        peopleWithActivity7Days: data.peopleWithActivity7Days,
        isLoading: false,
        lastFetchedAt: new Date().toISOString(),
      });
    } catch (error: any) {
      console.error('Failed to fetch dashboard stats:', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to load dashboard statistics',
      });
    }
  },

  /**
   * Fetch recently viewed people and organizations
   */
  fetchRecentViews: async () => {
    set({ isLoadingRecent: true });
    try {
      const [recentRes, churnedRes] = await Promise.all([
        api.get('/api/views/recent'),
        api.get('/api/people?stage=CHURNED&limit=10&sortBy=lastActivityAt&sortOrder=desc'),
      ]);
      set({
        recentPeople: recentRes.data.recentPeople,
        recentOrgs: recentRes.data.recentOrgs,
        churnedPeople: churnedRes.data.people || [],
        isLoadingRecent: false,
      });
    } catch {
      set({ isLoadingRecent: false });
    }
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set({
      pipeline: defaultPipeline,
      temperature: defaultTemperature,
      recentActivities: [],
      upcomingTasks: [],
      upcomingActivities: [],
      totalPeople: 0,
      peopleWithActivity7Days: 0,
      recentPeople: [],
      recentOrgs: [],
      churnedPeople: [],
      isLoadingRecent: false,
      isLoading: false,
      error: null,
      lastFetchedAt: null,
    });
  },
}));
